// apps/web/src/pages/vendas/detalhe/vincular-existente-dialog.tsx — Fase 12, Plano 11 (GOV-02, D-39, T-12-42).
// "Vincular lote ou remessa existente": lista os lotes SEM PV do mesmo cliente e aponta cada um para um item do PV do
// MESMO material. Sem backfill automático: o usuário escolhe. Ação exige pv:ajustar (autorização real no servidor).
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { LoteSemPv, PedidoVendaItemView, PedidoVendaView } from '@pieltec/contracts';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { FormMessage } from '../../../components/ui/form-message';
import { Skeleton } from '../../../components/ui/skeleton';
import { formatarDataLocal, formatarQuantidade } from '../../../lib/vendas/formatacao';
import { usePvComandos } from '../use-pv-comandos';
import { CampoSelect } from '../../../components/ui/campo-select';

interface VincularExistenteDialogProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly pv: PedidoVendaView;
  /** Chamado com o PV atualizado após o vínculo gravado. */
  readonly onVinculado: (pv: PedidoVendaView) => void;
  /** Recarrega o PV quando a versão ficou desatualizada (409). */
  readonly onRecarregar: () => void;
}

const MENSAGEM_FALHA_CARGA = 'Não foi possível carregar os lotes sem PV. Tente novamente.';

/** Itens do PV que aceitam o lote: não cancelados e com o mesmo material (a API recusa material diferente). */
export function itensCompativeis(lote: LoteSemPv, itens: readonly PedidoVendaItemView[]): PedidoVendaItemView[] {
  if (!lote.materialId) return [];
  return itens.filter((item) => !item.cancelado && item.materialId === lote.materialId);
}

function origemTexto(lote: LoteSemPv): string {
  const base = lote.origem === 'REMESSA' ? 'Remessa' : 'NF';
  return lote.documento ? `${base} ${lote.documento}` : base;
}

export function VincularExistenteDialog({ aberto, onAbertoChange, pv, onVinculado, onRecarregar }: VincularExistenteDialogProps) {
  const { vincularLotes } = usePvComandos();
  const [lotes, setLotes] = useState<LoteSemPv[] | null>(null);
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  const [selecionados, setSelecionados] = useState<ReadonlySet<string>>(new Set());
  const [itemPorLote, setItemPorLote] = useState<Readonly<Record<string, string>>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const carregar = useCallback(async (sinal: { vivo: boolean }) => {
    setLotes(null);
    setErroCarga(null);
    try {
      const res = await fetch(`/api/v1/vendas/pedidos/${encodeURIComponent(pv.id)}/lotes-sem-pv`, { credentials: 'include' });
      if (!sinal.vivo) return;
      if (!res.ok) {
        setErroCarga(res.status === 403 ? 'Você não tem permissão para vincular lotes.' : MENSAGEM_FALHA_CARGA);
        return;
      }
      const dados = (await res.json()) as LoteSemPv[];
      if (!sinal.vivo) return;
      setLotes(dados);
      // Pré-seleciona o item de mesmo material (o primeiro, na ordem do PV).
      const inicial: Record<string, string> = {};
      for (const lote of dados) {
        const primeiro = itensCompativeis(lote, pv.itens)[0];
        if (primeiro) inicial[lote.loteId] = primeiro.id;
      }
      setItemPorLote(inicial);
    } catch {
      if (sinal.vivo) setErroCarga(MENSAGEM_FALHA_CARGA);
    }
  }, [pv.id, pv.itens]);

  useEffect(() => {
    if (!aberto) return;
    setSelecionados(new Set());
    setErro(null);
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
    // Recarrega só ao abrir: mudanças posteriores do PV não devem descartar a seleção do usuário.
  }, [aberto]);

  const alternar = (loteId: string, marcado: boolean) => {
    setSelecionados((atual) => {
      const proximo = new Set(atual);
      if (marcado) proximo.add(loteId);
      else proximo.delete(loteId);
      return proximo;
    });
  };

  const vinculos = useMemo(
    () =>
      (lotes ?? [])
        .filter((lote) => selecionados.has(lote.loteId) && itemPorLote[lote.loteId])
        .map((lote) => ({ loteId: lote.loteId, pedidoVendaItemId: itemPorLote[lote.loteId]! })),
    [lotes, selecionados, itemPorLote],
  );

  async function vincular(): Promise<void> {
    if (vinculos.length === 0) {
      setErro('Selecione ao menos um lote e o item do PV.');
      return;
    }
    setErro(null);
    setEnviando(true);
    try {
      const resultado = await vincularLotes(pv.id, { versao: pv.versao, vinculos });
      if (resultado.tipo === 'ok') {
        onVinculado(resultado.resultado);
        onAbertoChange(false);
        return;
      }
      setErro(resultado.message);
      if (resultado.tipo === 'conflito') onRecarregar();
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={onAbertoChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Vincular lote ou remessa existente</DialogTitle>
          <DialogDescription>
            Lotes sem PV de {pv.clienteNome}. O andamento passa a ser acompanhado e fechado por este PV.
          </DialogDescription>
        </DialogHeader>

        {lotes === null && !erroCarga && (
          <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando lotes sem PV">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        )}

        {erroCarga && (
          <div role="alert" className="flex flex-col items-start gap-2">
            <FormMessage>{erroCarga}</FormMessage>
            <Button type="button" variant="outline" size="sm" onClick={() => void carregar({ vivo: true })}>
              Tentar novamente
            </Button>
          </div>
        )}

        {lotes !== null && lotes.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Nenhum lote sem PV para este cliente.</p>}

        {lotes !== null && lotes.length > 0 && (
          <div className="max-h-[50vh] overflow-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 text-left">
                <tr>
                  <th scope="col" className="w-10 px-3 py-2">
                    <span className="sr-only">Selecionar</span>
                  </th>
                  <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Lote</th>
                  <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Material</th>
                  <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Origem</th>
                  <th scope="col" className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider">Recebido</th>
                  <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Andamento</th>
                  <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Item do PV</th>
                </tr>
              </thead>
              <tbody>
                {lotes.map((lote) => {
                  const compativeis = itensCompativeis(lote, pv.itens);
                  const semItem = compativeis.length === 0;
                  const idItem = `vincular-item-${lote.loteId}`;
                  return (
                    <tr key={lote.loteId} className="border-t align-middle">
                      <td className="px-3 py-2">
                        <Checkbox
                          checked={selecionados.has(lote.loteId)}
                          disabled={semItem}
                          onCheckedChange={(valor) => alternar(lote.loteId, valor === true)}
                          aria-label={`Selecionar lote ${lote.loteInternoPieltec}`}
                        />
                      </td>
                      <td className="px-3 py-2 font-medium tabular-nums">{lote.loteInternoPieltec}</td>
                      <td className="px-3 py-2">
                        <span className="tabular-nums">{lote.materialCodigo}</span>
                        <span className="block text-xs text-muted-foreground">{lote.materialNome}</span>
                      </td>
                      <td className="px-3 py-2">
                        {origemTexto(lote)}
                        <span className="block text-xs text-muted-foreground tabular-nums">{formatarDataLocal(lote.recebidoEm)}</span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatarQuantidade(lote.recebido, 'un')}</td>
                      <td className="px-3 py-2">
                        {lote.etapaAtual ? lote.etapaAtual.nome : lote.opNumero !== null ? `OP ${lote.opNumero}` : 'Sem OP'}
                      </td>
                      <td className="px-3 py-2">
                        {semItem ? (
                          <span className="text-xs text-muted-foreground">Nenhum item do PV com este material</span>
                        ) : (
                          <>
                            <label htmlFor={idItem} className="sr-only">
                              Item do PV para o lote {lote.loteInternoPieltec}
                            </label>
                            <CampoSelect
                              id={idItem}
                              value={itemPorLote[lote.loteId] ?? ''}
                              onValueChange={(valor) => setItemPorLote((atual) => ({ ...atual, [lote.loteId]: valor }))}
                              options={compativeis.map((item) => ({ value: item.id, label: `Item ${item.numeroItem} · ${item.codigoItemCliente}` }))}
                              className="min-w-40"
                            />
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {erro && <FormMessage>{erro}</FormMessage>}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onAbertoChange(false)} disabled={enviando}>
            Voltar
          </Button>
          <Button type="button" onClick={() => void vincular()} disabled={enviando || vinculos.length === 0}>
            Vincular selecionados
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
